import React, { ReactElement } from 'react'
import { Container, useMediaQuery, useTheme } from '@mui/material'
import { Fade } from 'react-awesome-reveal'
import Image3 from 'images/wholesale/003.svg'
import Image4 from 'images/wholesale/004.svg'
import Image5 from 'images/wholesale/005.svg'
import Image6 from 'images/wholesale/006.svg'
import LineImage from 'images/wholesale/017.svg'
import LineMobileImage1 from 'images/wholesale/011.svg'
import LineMobileImage2 from 'images/wholesale/018.svg'
import { Trans, useTranslation } from 'react-i18next'

const TITLE_COLOR = '#F0AE16'
const BACKGROUND_COLOR = '#F2EEEA'

interface Block {
  id: string
  position: 'left' | 'right'
  title: string
  description: ReactElement | string
  image: string
}

interface IDoItSectionProps {}

const HowItAllSection: React.FC<IDoItSectionProps> = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const mdDown = useMediaQuery(theme.breakpoints.down('md'))

  const BLOCKS: { backgroundImage: string; nodes: Block[] }[] = [
    {
      backgroundImage: LineMobileImage1,
      nodes: [
        {
          id: '1',
          position: 'left',
          title: t('wholesale.registerAsWholesalePartner', 'Register as a Wholesale Partner'),
          description: (
            <Trans i18nKey='wholesale.registerAsWholesalePartnerDescription'>
              Fill out a short form and explain how you would like to sell our products. <br />
              We will contact you ASAP to confirm your application.
            </Trans>
          ),
          image: Image3,
        },
        {
          id: '2',
          position: 'left',
          title: t('wholesale.createOrder', 'Create an Order'),
          description: (
            <Trans i18nKey='wholesale.createOrderDescription'>
              You will create your order on the special wholesale page in your Account. <br />
              Choose specific items to customize your kits, or let us do it for you. <br />
              There you will see the merchandise total, weight and total cost with shipping. <br />
              Each order will be confirmed by our sales department.
            </Trans>
          ),
          image: Image4,
        },
      ],
    },
    {
      backgroundImage: LineMobileImage2,
      nodes: [
        {
          id: '3',
          position: 'right',
          title: t('wholesale.sitBackAndTrackYourOrder', 'Sit Back and Track Your Order'),
          description: (
            <Trans i18nKey='wholesale.sitBackAndTrackYourOrderDescription'>
              Your order goes to processing just after payment. <br />
              We will prepare your order ASAP and inform you before it ships.
            </Trans>
          ),
          image: Image5,
        },
        {
          id: '4',
          position: 'right',
          title: t('wholesale.receiveYourOrder', 'Receive Your Order'),
          description: (
            <Trans i18nKey='wholesale.receiveYourOrderDescription'>
              Finally, your customers will enjoy our designs. <br />
              It is an awesome moment for all of us!
            </Trans>
          ),
          image: Image6,
        },
      ],
    },
  ]
  return (
    <section className='pb-0 pt-20 text-center md:text-left' style={{ backgroundColor: BACKGROUND_COLOR }}>
      <Container maxWidth='lg'>
        <h2
          className='sticky top-0 text-5xl md:text-6xl text-center py-10 font-semibold z-10'
          style={{ backgroundColor: BACKGROUND_COLOR }}
        >
          {t('wholesale.howItAllComesTogether', 'How it All Comes Together')}
        </h2>
        <div
          className='mt-20 md:mt-50 overflow-hidden bg-contain md:bg-no-repeat md:bg-full'
          style={{ backgroundImage: !mdDown ? `url(${LineImage})` : 'none' }}
        >
          {BLOCKS.map((row, idx) => {
            return (
              <div
                key={idx}
                className='bg-contain bg-no-repeat md:bg-full'
                style={{ backgroundImage: mdDown ? `url(${row.backgroundImage})` : 'none' }}
              >
                {row.nodes.map(node => {
                  return (
                    <div key={node.id} className='flex flex-col md:flex-row mb-20 md:mb-40 relative'>
                      <div className={`md:w-1/2 md:${node.position === 'left' ? 'order-1' : 'order-2'}`}>
                        <Fade triggerOnce direction={node.position}>
                          <h2 className='text-3xl md:text-4xl mb-20 font-semibold' style={{ color: TITLE_COLOR }}>
                            {node.title}
                          </h2>
                          <p className='text-xl leading-normal hidden md:block max-w-450'>{node.description}</p>
                        </Fade>
                      </div>
                      <div className={`md:w-1/2 md:${node.position === 'left' ? 'order-2' : 'order-1'}`}>
                        <img
                          className='w-full'
                          src={node.image}
                          alt={node.title}
                          draggable={false}
                          style={{ maxHeight: 330 }}
                        />
                        <Fade triggerOnce direction={node.position}>
                          <p className='text-lg leading-normal block md:hidden'>{node.description}</p>
                        </Fade>
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}

export default HowItAllSection
